// TODO: DomainEvent should be imported from @swoft/core when available
// import { DomainEvent } from '@swoft/core';

/**
 * Temporary DomainEvent interface until proper import is available
 */
interface DomainEvent {
  readonly eventId: string;
  readonly aggregateId: string;
  readonly eventType: string;
  readonly occurredAt: Date;
  readonly eventVersion: number;
  getEventData(): Record<string, any>;
}

/**
 * GTD Domain Events following David Allen's methodology
 */

export class ProjectIdentified implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.ProjectIdentified';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly projectName: string,
    public readonly desiredOutcome: string,
    public readonly identifiedBy: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      projectName: this.projectName,
      desiredOutcome: this.desiredOutcome,
      identifiedBy: this.identifiedBy,
      identifiedAt: this.occurredOn.toISOString()
    };
  }
}

export class NextActionCreated implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.NextActionCreated';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly description: string,
    public readonly context: string,
    public readonly energyLevel: string,
    public readonly createdBy: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      description: this.description,
      context: this.context,
      energyLevel: this.energyLevel,
      createdBy: this.createdBy,
      createdAt: this.occurredOn.toISOString()
    };
  }
}

export class TaskAssigned implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.TaskAssigned';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly assignedTo: string,
    public readonly roleType: string,
    public readonly assignedAt: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      assignedTo: this.assignedTo,
      roleType: this.roleType,
      assignedAt: this.assignedAt
    };
  }
}

export class ProjectCompleted implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.ProjectCompleted';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly completedBy: string,
    public readonly completionNotes?: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      completedBy: this.completedBy,
      completionNotes: this.completionNotes,
      completedAt: this.occurredOn.toISOString()
    };
  }
}

export class NextActionCompleted implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.NextActionCompleted';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly completedBy: string,
    public readonly completionNotes?: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      completedBy: this.completedBy,
      completionNotes: this.completionNotes,
      completedAt: this.occurredOn.toISOString()
    };
  }
}

export class ReferenceDocumentAdded implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.ReferenceDocumentAdded';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly documentName: string,
    public readonly documentType: string,
    public readonly addedBy: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      documentName: this.documentName,
      documentType: this.documentType,
      addedBy: this.addedBy,
      addedAt: this.occurredOn.toISOString()
    };
  }
}

export class WeeklyReviewStarted implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.WeeklyReviewStarted';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly reviewId: string,
    public readonly conductedBy: string
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      reviewId: this.reviewId,
      conductedBy: this.conductedBy,
      startedAt: this.occurredOn.toISOString()
    };
  }
}

export class WeeklyReviewCompleted implements DomainEvent {
  readonly eventId: string = crypto.randomUUID();
  readonly aggregateId: string;
  readonly eventType: string = 'gtd.WeeklyReviewCompleted';
  readonly occurredAt: Date = new Date();
  readonly eventVersion: number = 1;
  readonly occurredOn: Date = new Date(); // For backward compatibility

  constructor(
    aggregateId: string,
    public readonly reviewId: string,
    public readonly reviewNotes: string,
    public readonly nextActionsIdentified: number
  ) {
    this.aggregateId = aggregateId;
  }

  getEventData(): Record<string, any> {
    return {
      reviewId: this.reviewId,
      reviewNotes: this.reviewNotes,
      nextActionsIdentified: this.nextActionsIdentified,
      completedAt: this.occurredOn.toISOString()
    };
  }
}

export class NextActionEventData {
  constructor(
    public readonly projectName: string,
    public readonly description: string,
    public readonly context: string,
    public readonly energyLevel: string,
    public readonly estimatedDuration?: number,
    public readonly tags?: string[]
  ) {}
}
